import { useState, useEffect, useMemo } from "react";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { auth } from "../../firebase";
import { db } from "../../firebase";

/* ================= HISTORY TABLE STYLES ================= */

const tableWrapper = {
  overflowX: "auto",
  WebkitOverflowScrolling: "touch",
};

function History({ sidebarTheme }) {
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 480);
  const [history, setHistory] = useState([]);

  const [filters, setFilters] = useState({
    operation: "all",
    date: "all", // all | today | 3 | 7 | custom
    from: "",
    to: "",
  });
  useEffect(() => {
    const onResize = () => {
      setIsSmallScreen(window.innerWidth <= 480);
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const historyTable = useMemo(
    () => ({
      table: {
        width: "100%",
        minWidth: isSmallScreen ? 600 : 900,
        borderCollapse: "collapse",
      },
      thead: {
        background: "rgba(124,58,237,0.08)",
      },
      th: {
        padding: isSmallScreen ? "6px 8px" : "12px",
        fontSize: isSmallScreen ? 11 : 13,
        fontWeight: 700,
        textAlign: "left",
        borderBottom: "1px solid rgba(148,163,184,0.25)",
        whiteSpace: "nowrap",
      },
      td: {
        padding: isSmallScreen ? "6px 8px" : "12px",
        fontSize: isSmallScreen ? 11 : 14,
        borderBottom: "1px solid rgba(148,163,184,0.18)",
        whiteSpace: "nowrap",
      },
      tr: {
        transition: "background 0.2s ease",
      },
      rowHover: {
        cursor: "pointer",
      },
    }),
    [isSmallScreen]
  );

  useEffect(() => {
    if (!auth.currentUser) return;

    const loadHistory = async () => {
      const uid = auth.currentUser.uid;

      const q = query(
        collection(db, "users", uid, "tests"),
        orderBy("createdAt", "desc")
      );

      const snap = await getDocs(q);

      const data = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      setHistory(data);
    };

    loadHistory();
  }, []);

  const filteredHistory = history.filter((t) => {
    /* 🔹 Operation filter (applies to BOTH normal & stream) */
    if (filters.operation !== "all") {
      if (t.operation !== filters.operation) return false;
    }

    /* 🔹 Date filter */
    const testDate = t.createdAt?.seconds
      ? new Date(t.createdAt.seconds * 1000)
      : new Date(t.createdAt);

    const now = new Date();

    if (filters.date !== "all" && filters.date !== "custom") {
      const days = Number(filters.date);
      const diff = (now - testDate) / (1000 * 60 * 60 * 24);
      if (diff > days) return false;
    }

    if (filters.date === "custom") {
      if (filters.from && testDate < new Date(filters.from)) return false;
      if (filters.to && testDate > new Date(filters.to)) return false;
    }

    return true;
  });

  // 🔢 Separate numbering for each test type
  const normalTests = filteredHistory
    .filter((t) => t.testType === "normal")
    .map((t, index) => ({
      ...t,
      __index: index + 1,
    }));

  const streamTests = filteredHistory
    .filter((t) => t.testType === "stream")
    .map((t, index) => ({
      ...t,
      __index: index + 1,
    }));

  return (
    <div>
      <h1 style={{ fontSize: 32, marginBottom: 15 }}>📊 Test History</h1>

      {/* ===== FILTER BAR ===== */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: isSmallScreen ? 6 : 10,
          margin: isSmallScreen ? "8px 0 12px" : "12px 0 20px",
          flexWrap: "wrap",
        }}
      >
        <span style={{ opacity: 0.7 }}>🧰 Filters</span>

        {/* Operation */}
        <select
          value={filters.operation}
          onChange={(e) =>
            setFilters({ ...filters, operation: e.target.value })
          }
          style={filterStyle(sidebarTheme, isSmallScreen)}
        >
          <option value="all">All Ops</option>
          <option value="add">Addition</option>
          <option value="sub">Subtraction</option>
          <option value="mul">Multiplication</option>
          <option value="div">Division</option>
          <option value="square">Square</option>
          <option value="cube">Cube</option>
          <option value="last1">Last digit </option>
          <option value="last2">Last two digits</option>
        </select>

        {/* Date */}
        <select
          value={filters.date}
          onChange={(e) => setFilters({ ...filters, date: e.target.value })}
          style={filterStyle(sidebarTheme, isSmallScreen)}
        >
          <option value="all">All Time</option>
          <option value="1">Today</option>
          <option value="3">Last 3 Days</option>
          <option value="7">Last 7 Days</option>
          <option value="custom">Custom</option>
        </select>

        {/* Custom Date Picker */}
        {filters.date === "custom" && (
          <>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters({ ...filters, from: e.target.value })}
              style={filterStyle(sidebarTheme, isSmallScreen)}
            />
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters({ ...filters, to: e.target.value })}
              style={filterStyle(sidebarTheme, isSmallScreen)}
            />
          </>
        )}

        {/* Reset Filters */}
        <button
          onClick={() =>
            setFilters({
              operation: "all",
              date: "all",
              from: "",
              to: "",
            })
          }
          style={filterStyle(sidebarTheme, isSmallScreen)}
        >
          ♻ Reset
        </button>
      </div>
      <div
        style={{
          textAlign: "center",
          marginTop: 10,
          marginBottom: 24, // ✅ ADD THIS LINE
        }}
      >
        <span style={sectionPill(sidebarTheme)}>Normal Test History</span>
      </div>

      {filteredHistory.length === 0 ? (
        <p style={{ opacity: 0.6 }}>No tests attempted yet</p>
      ) : (
        <>
          {/* ================= NORMAL TEST HISTORY TABLE ================= */}

          <div style={tableWrapper}>
            <table style={historyTable.table}>
              <thead style={historyTable.thead}>
                <tr>
                  <th style={historyTable.th}>#</th>
                  <th style={historyTable.th}>Test Name</th>
                  <th style={historyTable.th}>Date</th>
                  <th style={historyTable.th}>Operation</th>
                  <th style={historyTable.th}>Difficulty</th>
                  <th style={historyTable.th}>Accuracy</th>
                  <th style={historyTable.th}>Correct</th>
                  <th style={historyTable.th}>Total Questions</th>
                  <th style={historyTable.th}>Avg Time</th>
                  <th style={historyTable.th}>Fastest</th>
                  <th style={historyTable.th}>Slowest</th>
                  <th style={historyTable.th}>Total Time</th>
                </tr>
              </thead>

              <tbody>
                {normalTests.map((t, i) => (
                  <tr
                    key={i}
                    style={{ ...historyTable.tr, ...historyTable.rowHover }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background =
                        sidebarTheme.bgMain === "#020617"
                          ? "#0f172a"
                          : "#e5e7eb")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    <td style={historyTable.td}>{t.__index}</td>
                    <td style={historyTable.td}>{t.name}</td>
                    <td style={historyTable.td}>
                      {t.createdAt?.seconds
                        ? new Date(t.createdAt.seconds * 1000).toLocaleString()
                        : ""}
                    </td>
                    <td style={historyTable.td}>{t.operation}</td>
                    <td style={historyTable.td}>{t.difficulty}</td>
                    <td style={historyTable.td}>{t.accuracy}%</td>
                    <td style={historyTable.td}>{t.correct}</td>
                    <td style={historyTable.td}>{t.total}</td>
                    <td style={historyTable.td}>{t.avgTime}s</td>
                    <td style={historyTable.td}>
                      {t.fastest != null ? `${t.fastest}s` : "-"}
                    </td>
                    <td style={historyTable.td}>
                      {t.slowest != null ? `${t.slowest}s` : "-"}
                    </td>
                    <td style={historyTable.td}>{t.timeTaken}s</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ===== Divider between Normal & Stream History ===== */}
          <div
            style={{
              borderTop: `1px dashed ${sidebarTheme.border}`,
              margin: "40px 0 30px",
              opacity: 0.8,
            }}
          />

          {/* ================= STREAM TEST HISTORY ================= */}

          <div style={{ textAlign: "center", marginTop: 40 }}>
            <span style={sectionPill(sidebarTheme)}>Stream Test History</span>
          </div>

          {streamTests.length === 0 ? (
            <p style={{ opacity: 0.6, textAlign: "center" }}>
              No stream tests attempted yet
            </p>
          ) : (
            <div style={tableWrapper}>
              <table style={historyTable.table}>
                <thead>
                  <tr>
                    <th style={historyTable.th}>#</th>
                    <th style={historyTable.th}>Test Name</th>
                    <th style={historyTable.th}>Date & Time</th>
                    <th style={historyTable.th}>Operation</th>
                    <th style={historyTable.th}>Difficulty</th>
                    <th style={historyTable.th}>Total Questions</th>
                    <th style={historyTable.th}>Total Time</th>
                    <th style={historyTable.th}>Result</th>
                  </tr>
                </thead>

                <tbody>
                  {streamTests.map((t, i) => (
                    <tr key={i} style={historyTable.tr}>
                      <td style={historyTable.td}>{t.__index}</td>
                      <td style={historyTable.td}>{t.name}</td>
                      <td style={historyTable.td}>
                        {t.date?.seconds
                          ? new Date(t.date.seconds * 1000).toLocaleString()
                          : t.date}
                      </td>
                      <td style={historyTable.td}>{t.operation}</td>
                      <td style={historyTable.td}>{t.difficulty}</td>
                      <td style={historyTable.td}>{t.total}</td>
                      <td style={historyTable.td}>{t.timeTaken}s</td>
                      <td
                        style={{
                          ...historyTable.td,
                          fontWeight: 600,
                          color:
                            Number(t.correct) === Number(t.total)
                              ? "#22c55e"
                              : "#ef4444",
                        }}
                      >
                        {Number(t.correct) === Number(t.total)
                          ? "Correct"
                          : "Wrong"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const sectionPill = (theme) => ({
  display: "inline-block",
  padding: "8px 18px",
  borderRadius: 999,
  background:
    theme.bgMain === "#020617"
      ? "rgba(148,163,184,0.12)"
      : "rgba(15,23,42,0.08)",
  border: `1px solid ${theme.border}`,
  fontSize: 18,
  fontWeight: 600,
  letterSpacing: "0.3px",
});

const filterStyle = (theme, isSmallScreen) => ({
  background: theme.card,
  color: theme.text,
  border: `1px solid ${theme.border}`,
  borderRadius: 10,
  padding: isSmallScreen ? "4px 8px" : "6px 10px",
  fontSize: isSmallScreen ? 11 : 13,
  outline: "none",
});

export default History;
